import { Dialog, DialogContent, DialogTitle, RiFileUploadLine, RiMapPinLine, Stack } from 'component-library';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useSetOrganizationId } from '@/pages/shared/hooks/useOrganizationId';

import { usePlotPaths } from '../pages/upload-plots/hooks/usePlotPaths';

type AddPlotsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
};

export const AddPlotsModal = ({ open, onOpenChange, organizationId }: AddPlotsModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setOrganizationId = useSetOrganizationId();
  const plotPaths = usePlotPaths();

  const handleSelect = (destination: string) => {
    setOrganizationId(organizationId);
    navigate(destination);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl' data-testid='add-plots-modal'>
        <DialogTitle className='mb-8 text-center'>{t('shared.organizations.addPlotsModal.title')}</DialogTitle>
        <Stack direction='row' className='gap-6'>
          <UploadOptionCard
            icon={<RiFileUploadLine size={32} />}
            title={t('shared.organizations.addPlotsModal.fileUpload.title')}
            description={t('shared.organizations.addPlotsModal.fileUpload.description')}
            data-testid='add-plots-file-upload-card'
            onClick={() => handleSelect(plotPaths.plotUpload)}
          />
          <UploadOptionCard
            icon={<RiMapPinLine size={32} />}
            title={t('shared.organizations.addPlotsModal.manualPin.title')}
            description={t('shared.organizations.addPlotsModal.manualPin.description')}
            data-testid='add-plots-manual-pin-card'
            onClick={() => handleSelect(plotPaths.coordinateUpload)}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

type UploadOptionCardProps = {
  icon: ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  'data-testid'?: string;
};

const UploadOptionCard = ({ icon, title, description, onClick, 'data-testid': testId }: UploadOptionCardProps) => (
  <button
    type='button'
    data-testid={testId}
    onClick={onClick}
    className='flex flex-1 h-[200px] flex-col items-center justify-center gap-4 rounded-2xl border border-neutral-black-12 p-8 text-center transition-colors hover:border-primary-100 hover:bg-neutral-black-4 cursor-pointer'
  >
    <span className='text-primary-100'>{icon}</span>
    <Stack className='gap-1 items-center'>
      <span className='typography-body1 font-medium'>{title}</span>
      <span className='typography-body2 text-text-secondary'>{description}</span>
    </Stack>
  </button>
);
